/**
 * Email outbox + Tools-tab switcher — Phase 2.3.j.6.
 *
 * Owns the "Email outbox" tab inside the Tools view (lists outbound
 * email messages with their AI-review state and ticket linkage) and the
 * shared Tools-tab switcher (`setActiveToolsTab`) that toggles between
 * the Duplicate-messages tab and this one.
 *
 * Loaded as a module before admin.js. The IIFE registers
 * `window.adminViews.emailOutbox`. Listeners are wired by `attachListeners`
 * at admin.js boot time so the legacy semantics (listeners attached at
 * module-load) are preserved.
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let toolsTabBar, toolsTabPanels,
      emailOutboxTbody, emailOutboxStatus, emailOutboxRefreshBtn;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    toolsTabBar = document.getElementById('toolsTabBar');
    toolsTabPanels = {
      duplicates: document.getElementById('toolsDuplicatesPanel'),
      'email-outbox': document.getElementById('toolsEmailPanel'),
      'revenue-compliance': document.getElementById('toolsRevenueCompliancePanel'),
      'hr-compliance': document.getElementById('toolsHrCompliancePanel'),
    };
    emailOutboxTbody = document.getElementById('emailOutboxTbody');
    emailOutboxStatus = document.getElementById('emailOutboxStatus');
    emailOutboxRefreshBtn = document.getElementById('emailOutboxRefreshBtn');
  }

  // ---- State ----------------------------------------------------------
  const emailOutboxState = {
    loading: false,
    loaded: false,
    items: [],
    error: null,
  };

    function setActiveToolsTab(key) {
      if (!toolsTabPanels) return;
      const tabs = toolsTabBar ? Array.from(toolsTabBar.querySelectorAll('.tab[data-tools-tab]')) : [];
      tabs.forEach((tab) => {
        tab.classList.toggle('active', tab.dataset.toolsTab === key);
      });
      Object.entries(toolsTabPanels).forEach(([panelKey, panel]) => {
        if (!panel) return;
        panel.hidden = panelKey !== key;
      });
      if (key === 'email-outbox') {
        loadEmailOutbox().catch(() => {});
      } else if (key === 'revenue-compliance') {
        const rc = window.adminViews && window.adminViews.revenueCompliance;
        if (rc) rc.mount();
      } else if (key === 'hr-compliance') {
        const hr = window.adminViews && window.adminViews.hrReport;
        if (hr) hr.mount();
      }
    }

    function renderEmailOutbox() {
      if (!emailOutboxTbody) return;
      const { loading, loaded, items, error } = emailOutboxState;
      if (emailOutboxStatus) {
        const parts = [];
        if (loading) parts.push('Loading outgoing email messages…');
        if (!loading && error) parts.push(error);
        emailOutboxStatus.textContent = parts.join(' • ');
        emailOutboxStatus.hidden = !(emailOutboxStatus.textContent);
      }
      if (emailOutboxRefreshBtn) {
        emailOutboxRefreshBtn.disabled = loading;
      }
      if (loading) {
        emailOutboxTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
        return;
      }
      if (!loaded) {
        emailOutboxTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Switch to this tab to load messages.</td></tr>';
        return;
      }
      if (error) {
        emailOutboxTbody.innerHTML = `<tr><td colspan="6" style="color:var(--err);">${escapeHtml(error)}</td></tr>`;
        return;
      }
      if (!items.length) {
        emailOutboxTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">No outgoing email messages found.</td></tr>';
        return;
      }
      const rows = items.map((item) => {
        const sent = item.created_at ? formatDateTimeFull(item.created_at) : '—';
        const channel = item.channel ? String(item.channel).toUpperCase() : '—';
        const ticketLabel = item.ticket_id ? String(item.ticket_id).slice(0, 8) : '—';
        const subject = item.ticket_subject || '—';
        const author = item.author_name || '—';
        const review = (item.review_state || '').replace(/_/g, ' ') || 'pending';
        const flags = Array.isArray(item.quality_flags) ? item.quality_flags : [];
        const badgeParts = [];
        if (item.ai_review_accepted) {
          badgeParts.push('<span class="badge ok">AI accepted</span>');
        } else if (review && review.toLowerCase() === 'rejected') {
          badgeParts.push('<span class="badge warn">AI rejected</span>');
        }
        if (flags.length) {
          badgeParts.push(
            `<span class="badge warn" title="${escapeHtml(flags.join(', '))}">${escapeHtml(flags.length === 1 ? 'Flag' : `${flags.length} flags`)}</span>`,
          );
        }
        const badge = badgeParts.join('<div style="margin-top:4px;"></div>');
        const preview = item.body ? escapeHtml(item.body.slice(0, 200)) : '—';
        return `
          <tr>
            <td>${escapeHtml(sent)}</td>
            <td>${escapeHtml(channel)}</td>
            <td>${escapeHtml(ticketLabel)}</td>
            <td>${escapeHtml(subject)}</td>
            <td>${escapeHtml(author)}</td>
            <td>${escapeHtml(review)}${badge ? `<div style="margin-top:4px;">${badge}</div>` : ''}</td>
            <td>${preview}</td>
          </tr>
        `;
      });
      emailOutboxTbody.innerHTML = rows.join('');
    }

    async function loadEmailOutbox({ force = false } = {}) {
      if (emailOutboxState.loading) return;
      if (emailOutboxState.loaded && !force) return;
      emailOutboxState.loading = true;
      emailOutboxState.error = null;
      renderEmailOutbox();
      try {
        const payload = await adminAPI.fetch(API.adminEmailOutbox(), { method: 'GET' });
        const items = Array.isArray(payload && payload.items) ? payload.items : [];
        emailOutboxState.items = items;
        emailOutboxState.loaded = true;
      } catch (err) {
        emailOutboxState.error = err && err.message ? err.message : 'Failed to load outgoing messages';
        emailOutboxState.loaded = false;
      } finally {
        emailOutboxState.loading = false;
        renderEmailOutbox();
      }
    }

  function attachListeners() {
    resolveDom();
    if (toolsTabBar) {
      toolsTabBar.addEventListener('click', (event) => {
        const tab = event.target.closest('.tab[data-tools-tab]');
        if (!tab) return;
        event.preventDefault();
        setActiveToolsTab(tab.dataset.toolsTab);
      });
    }
    if (emailOutboxRefreshBtn) {
      emailOutboxRefreshBtn.addEventListener('click', (event) => {
        event.preventDefault();
        loadEmailOutbox({ force: true }).catch(() => {});
      });
    }
  }

export default {
  attachListeners,
  setActiveToolsTab,
  load: loadEmailOutbox,
  render: renderEmailOutbox,
  state: emailOutboxState,
};
